
export function fixResumeIfEntities(html = "") {
    if (!html || typeof html !== "string") return "";

    // Quick heuristics
    const entityPattern = /&quot;/;

    if (!entityPattern.test(html)) return html; // fast path: nothing to fix
    // --- helpers ---
    const decodeEntities = s => {
        if (!s) return "";
        s = s.replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&amp;/g, "&")
            .replace(/&nbsp;/g, " ").replace(/&lt;/g, "<").replace(/&gt;/g, ">");
        s = s.replace(/&#(\d+);/g, (_, d) => {
            try { return String.fromCharCode(Number(d)); } catch { return ""; }
        });
        s = s.replace(/&#x([0-9a-fA-F]+);/g, (_, h) => {
            try { return String.fromCharCode(parseInt(h, 16)); } catch { return ""; }
        });
        return s;
    };
    const esc = s => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const cleanWs = s => String(s || "").replace(/\u00A0/g, " ").replace(/\t/g, " ").replace(/\r/g, "");
    const collapse = s => s.replace(/[ \t]{2,}/g, " ").trim();

    // --- extract body (ignore noisy head JSON) ---
    let bodySnippet = "";
    const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    if (bodyMatch) bodySnippet = bodyMatch[1];
    else {
        const afterHead = html.split(/<\/head>/i);
        bodySnippet = afterHead.length > 1 ? afterHead.slice(1).join("</head>") : html;
    }
    bodySnippet = decodeEntities(bodySnippet);

    // --- convert tags to visible separators and remove scripts/styles ---
    bodySnippet = bodySnippet
        .replace(/<(?:script|style)[^>]*>[\s\S]*?<\/(?:script|style)>/gi, "")
        .replace(/<(?:\/?(?:p|div|section|article|header|footer|h[1-6]|li|tr|table|thead|tbody|tfoot|br|hr)[^>]*)>/gi, "\n")
        .replace(/<(?:td|th)[^>]*>/gi, "\t")
        .replace(/<[^>]+>/g, " ");

    // Normalize whitespace, collapse many newlines to paragraphs
    bodySnippet = cleanWs(bodySnippet).replace(/\n{3,}/g, "\n\n");
    bodySnippet = bodySnippet.split("\n").map(line => collapse(line)).join("\n");
    bodySnippet = bodySnippet.replace(/^\s+|\s+$/g, "");
    bodySnippet = bodySnippet.replace(/\n{2,}/g, "\n\n");

    // Remove pure-bullet / decorative-only paragraphs (e.g., "• • •", "···", "•")
    const parasRaw = bodySnippet.split(/\n{2,}/).map(p => p.trim()).filter(Boolean);
    const paragraphs = parasRaw.filter(p => {
        // if paragraph is only bullet-like characters or sequences of punctuation -> drop
        if (/^[·•\u2022\-\*\s\.,]{1,}$/.test(p)) return false;
        // if it's like "• • •" or "• •" (only bullets separated by spaces) drop
        if (/^(?:[·•\u2022]\s*){2,}$/.test(p)) return false;
        return true;
    });

    // --- header extraction (name/email/phone/linkedin/title) ---
    let name = "", email = "", phone = "", linkedin = "", title = "";
    const topJoin = paragraphs.slice(0, 5).join(" | ");
    const emailM = topJoin.match(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,})/);
    if (emailM) email = emailM[0];
    const phoneM = topJoin.match(/(\+?\d[\d\-\s().]{6,}\d)/);
    if (phoneM) phone = phoneM[0];
    const linkedinM = topJoin.match(/(https?:\/\/(www\.)?linkedin\.com\/[^\s,]+)/i);
    if (linkedinM) linkedin = linkedinM[0];

    if (paragraphs.length) {
        const first = paragraphs[0];
        if (!/@/.test(first) && !/\d{3,}/.test(first) && first.split(/\s+/).length <= 6) {
            name = first;
        } else {
            const tokens = first.split(/[|,•·\t]/).map(t => t.trim()).filter(Boolean);
            name = tokens.find(t => t && !/@/.test(t) && !/\d/.test(t) && t.split(/\s+/).length <= 6) || tokens[0] || "";
        }
    }

    // title detection
    for (let i = 0; i < Math.min(4, paragraphs.length); i++) {
        const p = paragraphs[i];
        if (/\b(Sr\.|Senior|Associate|Lead|Manager|Director|Engineer|Analyst|Developer|Full Stack|Program Manager|Java)\b/i.test(p) && p.length < 220) {
            if (!p.includes(email) && !p.includes(phone)) { title = p; break; }
        }
    }

    // --- group into sections ---
    const headingKeywords = [
        "RELEVANT QUALIFICATIONS", "SUMMARY", "TECHNICAL SKILLS", "SKILLS", "SKILLS/COMPETENCIES",
        "PROFESSIONAL EXPERIENCE", "WORK EXPERIENCE", "EMPLOYMENT HISTORY",
        "EDUCATION", "CERTIFICATIONS", "KEY ACHIEVEMENTS", "ACHIEVEMENTS", "PROJECTS"
    ];
    const sections = {};
    let current = "SUMMARY";
    for (let i = 0; i < paragraphs.length; i++) {
        const p = paragraphs[i];
        if (!p) continue;
        // skip header lines already used
        if (i < 4 && (p === name || p === email || p === phone || p === title || p === linkedin)) continue;

        const onlyCaps = p.replace(/[^A-Z\s]/g, "").trim();
        const isHeading = headingKeywords.some(k => new RegExp("\\b" + k.replace(/[^\w\s]/g, "") + "\\b", "i").test(p))
            || (onlyCaps && (onlyCaps.length / Math.max(1, p.length) > 0.6) && onlyCaps.split(/\s+/).length <= 6);

        if (isHeading) {
            let key = p.toUpperCase().trim();
            if (/RELEVANT/i.test(key)) key = "RELEVANT QUALIFICATIONS";
            else if (/TECHNICAL|SKILL/i.test(key) && /COMPET/i.test(key)) key = "SKILLS/COMPETENCIES";
            else if (/TECHNICAL|SKILL/i.test(key)) key = "TECHNICAL SKILLS";
            else if (/EMPLOY|PROFESS|WORK|EXPERIENCE/i.test(key)) key = "PROFESSIONAL EXPERIENCE";
            else if (/EDUCATION/i.test(key)) key = "EDUCATION";
            else if (/ACHIEVE/i.test(key)) key = "KEY ACHIEVEMENTS";
            current = key;
            sections[current] = sections[current] || [];
            continue;
        }
        sections[current] = sections[current] || [];
        sections[current].push(p);
    }

    // --- rendering helpers (compact output) ---
    const renderList = s => {
        const items = s.split(/(?:\s*·\s*|\s*•\s*|\u2022|;|\n|(?:\s*-\s+)|(?:\d+\.\s*))/)
            .map(x => x.trim()).filter(Boolean);
        if (!items.length) return `<p>${esc(s)}</p>`;
        return `<ul>${items.map(it => `<li>${esc(it)}</li>`).join("")}</ul>`;
    };
    const renderParagraph = p => {
        if (!p) return "";
        if (/·|•|\u2022|^[-*]\s*/.test(p) || p.split(/·|•|\u2022/).length > 1) return renderList(p);
        if (p.length > 120 && p.split(/,|;/).length > 5) return renderList(p);
        return `<p>${esc(p)}</p>`;
    };
    const buildQualTable = arr => {
        const rows = arr.map(p => {
            const parts = p.split(/\s{2,}|\t|[:—–-]{1,2}/).map(x => x.trim()).filter(Boolean);
            if (parts.length >= 2) return { l: parts[0], r: parts.slice(1).join(" - ") };
            const bullets = p.split(/·|•/).map(s => s.trim()).filter(Boolean);
            if (bullets.length > 1) return { l: bullets[0].split(/[.,]/)[0].slice(0, 80), r: bullets.slice(1).join(". ") };
            return { l: "", r: p };
        });
        return `<table><tbody>${rows.map(r => `<tr><td style="width:28%"><p><b>${esc(r.l)}</b></p></td><td>${renderParagraph(r.r)}</td></tr>`).join("")}</tbody></table>`;
    };
    const parseExperienceBlock = blk => {
        const lines = blk.split(/\. {1,}|\n/).map(l => l.trim()).filter(Boolean);
        const dateRx = /(\b\d{4}\b|\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)[^\d]{0,15}\d{4})/i;
        let date = "";
        for (let i = 0; i < Math.min(3, lines.length); i++) {
            const m = lines[i].match(dateRx);
            if (m) { date = lines[i]; lines.splice(i, 1); break; }
        }
        const company = lines[0] || "";
        const role = lines[1] || "";
        const desc = lines.slice(2).join(". ") || lines.slice(1).join(". ");
        return { company: company.trim(), role: role.trim(), date: date.trim(), desc: desc.trim() };
    };

    // --- improved CSS (tighter spacing) ---
const css = `
  :root {
    --accent-color: #0f172a;
    --text-color: #111;
    --subtext-color: #444;
    --font-family: 'Inter', Arial, Helvetica, sans-serif;
    --max-width: 900px;
  }

  body {
    font-family: var(--font-family);
    line-height: 1.6;
    color: var(--text-color);
    padding: 36px;
    margin: 0 auto;
    background: #fff;
    max-width: var(--max-width);
  }

  header {
    margin-bottom: 24px;
    border-bottom: 2px solid #e5e7eb;
    padding-bottom: 12px;
  }

  .name {
    font-weight: 700;
    font-size: 22px;
    color: var(--accent-color);
    margin-bottom: 6px;
    letter-spacing: 0.4px;
  }

  .contact {
    font-size: 14px;
    color: var(--subtext-color);
    margin-bottom: 6px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .contact span::after {
    content: ",";
    margin-right: 6px;
  }

  .contact span:last-child::after {
    content: "";
  }

  .title {
    font-size: 14px;
    color: #333;
    margin-top: 4px;
  }

  h2 {
    font-size: 15px;
    margin: 26px 0 12px;
    font-weight: 700;
    color: var(--accent-color);
    text-transform: uppercase;
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 4px;
    letter-spacing: 0.6px;
  }

  p {
    margin: 6px 0 10px;
    font-size: 14px;
    color: #1f2937;
  }

  ul {
    margin: 8px 0 12px 24px;
    padding: 0;
    list-style-type: disc;
  }

  li {
    margin: 4px 0;
    font-size: 14px;
    line-height: 1.45;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 12px 0 18px;
  }

  td {
    vertical-align: top;
    padding: 8px 12px;
  }

  .company {
    font-weight: 700;
    color: var(--accent-color);
    margin-bottom: 4px;
  }

  .role {
    font-weight: 600;
    color: #222;
    margin-bottom: 4px;
  }

  .date {
    font-size: 13px;
    color: #666;
    margin-top: 4px;
  }

  .list_Paragraph {
    margin: 6px 0;
    text-indent: -1em;
    padding-left: 1.5em;
    font-size: 14px;
  }

  @media print {
    body {
      padding: 0;
      font-size: 12px;
      color: #000;
    }
    header {
      border-bottom: none;
      margin-bottom: 10px;
    }
  }

  @media (max-width: 640px) {
    body {
      padding: 18px;
      font-size: 13px;
    }
    td {
      display: block;
      width: 100%;
      padding: 4px 0;
    }
  }
`;


    // --- assemble final HTML ---
    const out = [];
    out.push("<!doctype html><html><head><meta charset='utf-8'><title>Resume</title>");
    out.push(`<style>${css}</style></head><body><div class="container">`);

    // header
    out.push("<header>");
    if (name) out.push(`<div class="name">${esc(name)}</div>`);
    const contactParts = [];
    if (email) contactParts.push(email);
    if (phone) contactParts.push(phone);
    if (linkedin) contactParts.push(linkedin);
    if (contactParts.length) out.push(`<div class="contact">${esc(contactParts.join(" | "))}</div>`);
    if (title) out.push(`<div class="title">${esc(title)}</div>`);
    out.push("</header>");

    // preferred order
    const order = ["SUMMARY", "RELEVANT QUALIFICATIONS", "TECHNICAL SKILLS", "SKILLS/COMPETENCIES", "KEY ACHIEVEMENTS", "PROFESSIONAL EXPERIENCE", "EDUCATION", "OTHER"];
  // Inside your main loop, replace this part:
for (const key of order) {
  if (!sections[key] || sections[key].length === 0) continue;
  out.push(`<h2>${esc(key)}</h2>`);

  if (key === "RELEVANT QUALIFICATIONS") {
    out.push(buildQualTable(sections[key]));
    continue;
  }

  // ✅ FIXED SUMMARY + SKILLS FORMATTING
if (key === "SUMMARY") {
  // join paragraphs into a single raw summary string
  let raw = sections[key].join(" ").replace(/\s+/g, " ").trim();

  // extract contact-like pieces
  const emailRx = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,})/;
  const phoneRx = /(\+?\d[\d\-\s().]{6,}\d)/;
  const linkedinRx = /(https?:\/\/(www\.)?linkedin\.com\/[^\s,;]+)/i;

  const partsSet = new Set();

  // helper to push unique, trimmed parts
  const pushPart = (s) => {
    if (!s) return;
    const t = s.replace(/^[\s,;|•·\u2022-]+|[\s,;|•·\u2022-]+$/g, "").trim();
    if (t) partsSet.add(t);
  };

  // find & remove email
  const emailMatch = raw.match(emailRx);
  if (emailMatch) {
    pushPart(emailMatch[0]);
    raw = raw.replace(emailMatch[0], " ");
  }

  // find & remove linkedin
  const liMatch = raw.match(linkedinRx);
  if (liMatch) {
    pushPart(liMatch[0]);
    raw = raw.replace(liMatch[0], " ");
  }

  // find & remove phone (take first sensible match)
  const phoneMatch = raw.match(phoneRx);
  if (phoneMatch) {
    // normalize spaces in phone
    const phoneRaw = phoneMatch[0].replace(/\s+/g, " ").trim();
    pushPart(phoneRaw);
    raw = raw.replace(phoneMatch[0], " ");
  }

  // Now split the leftover into sensible tokens:
  // keep existing comma-separated phrases and also split on common separators
  // but preserve phrases that look like "City, ST" by splitting first on line breaks or ' · ' etc,
  // then split comma groups only when they are not 'City, ST' (approx).
  raw = raw.replace(/[·•\u2022|\/]/g, ","); // unify separators to commas
  raw = raw.replace(/\s{2,}/g, " ").trim();

  // split by commas, but merge tokens that look like "Word1 Word2" (no extra splitting)
  const rawTokens = raw.split(",").map(t => t.trim()).filter(Boolean);

  // push tokens but avoid pushing tiny fragments like single punctuation
  for (let t of rawTokens) {
    // remove stray punctuation at ends
    t = t.replace(/^[\s;:\-–—]+|[\s;:\-–—]+$/g, "").trim();
    if (!t) continue;
    // skip if it's just numeric garbage
    if (/^[\-\u2022·•\s,.;:]+$/.test(t)) continue;
    // if token contains an email/phone/linkedin leftover, skip (already handled)
    if (emailRx.test(t) || phoneRx.test(t) || linkedinRx.test(t)) continue;
    pushPart(t);
  }

  // If partsSet ended up empty (rare), fallback to cleaned raw as a single paragraph
  let finalParts = Array.from(partsSet);
  if (finalParts.length === 0 && raw) {
    finalParts = [raw.trim()];
  }

  // Join with commas, ensure spacing after commas
  const summaryText = finalParts.join(", ").replace(/\s{2,}/g, " ").trim();

  out.push(`<p>${esc(summaryText)}</p>`);
  continue;
}


if (key === "TECHNICAL SKILLS" || key === "SKILLS/COMPETENCIES") {
  // join and normalize whitespace
  const merged = sections[key].join(" ").replace(/\s+/g, " ").trim();

  // unify common separators into commas
  const unified = merged.replace(/[·•\u2022|\/;•·]/g, ",").replace(/[\u2013\u2014]/g, "-");

  // collapse multiple commas/spaces
  const cleaned = unified.replace(/\s*,\s*/g, ",").replace(/,+/g, ",").replace(/^\s*,|,\s*$/g, "").trim();

  // split into candidate skill tokens
  const rawItems = cleaned.split(",").map(s => s.trim()).filter(Boolean);

  // helper: preserve acronyms / tokens with numbers/symbols; otherwise Title Case
  const formatSkill = (s) => {
    // If it's already mostly uppercase acronym or contains digits/symbols, keep as-is (but trim)
    if (/^[A-Z0-9\-\+_.#\/]{1,}$/.test(s) || /[0-9]/.test(s) || /[\/#.+\-]/.test(s)) return s;
    // If it looks like an acronym with mixed letters (e.g., "SQL", "AWS"), preserve uppercase
    if (/^[A-Z]{2,}$/.test(s)) return s;
    // Otherwise, Title Case (safe)
    return s
      .toLowerCase()
      .split(/\s+/)
      .map(w => w.length <= 2 ? w.toUpperCase() : (w.charAt(0).toUpperCase() + w.slice(1)))
      .join(" ");
  };

  // dedupe while preserving order
  const seen = new Set();
  const items = [];
  for (let it of rawItems) {
    // remove stray leading bullets/digits/extra punctuation
    it = it.replace(/^[\s\-\u2022·•\d\.\)]+/, "").replace(/[\s\-\u2022·•\.\)]+$/g, "").trim();
    if (!it) continue;
    const formatted = formatSkill(it);
    const keyNorm = formatted.toLowerCase();
    if (!seen.has(keyNorm)) {
      seen.add(keyNorm);
      items.push(formatted);
    }
  }

  // render as comma-separated spans (escaped)
  out.push(
    `<div class="skills">${items.map(s => `<span>${esc(s)}</span>`).join(", ")}</div>`
  );
  continue;
}


  if (key === "PROFESSIONAL EXPERIENCE") {
    out.push("<table><tbody>");
    for (const blk of sections[key]) {
      const p = parseExperienceBlock(blk);
      if (!p.company && !p.role)
        out.push(`<tr><td colspan="2">${renderParagraph(blk)}</td></tr>`);
      else {
        const left = [];
        if (p.company) left.push(`<div class="company">${esc(p.company)}</div>`);
        if (p.date) left.push(`<div class="date">${esc(p.date)}</div>`);
        const right = [];
        if (p.role) right.push(`<div class="role">${esc(p.role)}</div>`);
        if (p.desc) right.push(renderList(p.desc));
        out.push(
          `<tr><td style="width:32%">${left.join(
            ""
          )}</td><td>${right.join("")}</td></tr>`
        );
      }
    }
    out.push("</tbody></table>");
    continue;
  }

  // Default paragraph rendering
  for (const p of sections[key]) out.push(renderParagraph(p));
}


    // leftovers
    for (const [k, paras] of Object.entries(sections)) {
        if (order.includes(k) || !paras || paras.length === 0) continue;
        out.push(`<h2>${esc(k)}</h2>`);
        for (const p of paras) out.push(renderParagraph(p));
    }

    out.push("</div></body></html>");
    return out.join("\n");
}
